// ============================================================================
// Ingresos leídos con IA (#1274)
// ============================================================================
// Desde el modal «Registrar ingreso» se adjunta un comprobante y la IA propone
// los ingresos. El documento es de uno de dos tipos: un ESTADO DE CUENTA (varios
// depósitos) o UNA SOLA OPERACIÓN (comprobante de una transferencia o depósito).
// El tipo lo dice quien sube (dropdown) y también lo identifica la IA; si no
// coinciden, manda lo que dijo la persona y se avisa. La IA propone: aquí se
// decide qué es un ingreso válido y cuál ya estaba registrado.
// ============================================================================

import { BASE_CURRENCY, INCOME_PAYER_TYPES, isIsoDate, round2, type Income, type IncomePayerType } from "./index";

/** Tipo de documento del que sale un ingreso. */
export const INCOME_DOCUMENT_KINDS = ["estado_de_cuenta", "operacion"] as const;
export type IncomeDocumentKind = (typeof INCOME_DOCUMENT_KINDS)[number];

/** Lo que elige quien sube: un tipo, o que lo identifique la IA. */
export const DOCUMENT_KIND_CHOICES = ["auto", ...INCOME_DOCUMENT_KINDS] as const;
export type DocumentKindChoice = (typeof DOCUMENT_KIND_CHOICES)[number];

export const DOCUMENT_KIND_LABELS: Record<DocumentKindChoice, { es: string; en: string }> = {
  auto: { es: "Detectar automáticamente", en: "Detect automatically" },
  estado_de_cuenta: { es: "Estado de cuenta", en: "Bank statement" },
  operacion: { es: "Una sola operación", en: "A single transaction" },
};

/** `auto` (o cualquier valor desconocido) = sin declarar. */
export function declaredDocumentKind(value: unknown): IncomeDocumentKind | null {
  return INCOME_DOCUMENT_KINDS.includes(value as IncomeDocumentKind) ? (value as IncomeDocumentKind) : null;
}

export function isDocumentKindChoice(value: unknown): value is DocumentKindChoice {
  return DOCUMENT_KIND_CHOICES.includes(value as DocumentKindChoice);
}

export interface ExtractedIncome {
  date: string | null;
  amount: number;
  currency: string;
  exchangeRate: number | null;
  payer: string;
  payerType: IncomePayerType;
  concept: string | null;
  bank: string | null;
  confidence: "alta" | "media" | "baja";
}

export interface IncomeExtraction {
  /** El tipo con que se trata el documento: lo declarado o, sin declarar, lo detectado. */
  documentType: IncomeDocumentKind;
  /** Lo que identificó la IA (`null` si no lo pudo decir). */
  detectedType: IncomeDocumentKind | null;
  /** La persona declaró un tipo y la IA identificó el otro. */
  mismatch: boolean;
  issuer: string | null;
  /** Cuenta donde entró el dinero, como la trae el documento. */
  account: string | null;
  incomes: ExtractedIncome[];
  notes: string | null;
}

const text = (v: unknown, max = 200): string | null => {
  const s = typeof v === "string" ? v.trim().replace(/\s+/g, " ").slice(0, max) : "";
  return s || null;
};

/**
 * Limpia lo que devuelve la IA. Se descartan los renglones sin importe positivo;
 * un ingreso sin pagador legible queda como «Sin identificar» y con confianza
 * baja, para que la persona lo corrija antes de guardar.
 */
export function sanitizeIncomeExtraction(raw: unknown, { declared = null }: { declared?: IncomeDocumentKind | null } = {}): IncomeExtraction {
  const data = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const list = Array.isArray(data.incomes) ? data.incomes : [];
  const incomes: ExtractedIncome[] = [];
  for (const item of list) {
    if (!item || typeof item !== "object") continue;
    const r = item as Record<string, unknown>;
    // Un importe negativo es dinero que salió: no se convierte en ingreso.
    const amount = round2(Number(r.amount));
    if (!Number.isFinite(amount) || amount <= 0) continue;
    const date = typeof r.date === "string" && isIsoDate(r.date.slice(0, 10)) ? r.date.slice(0, 10) : null;
    const currency = /^[A-Za-z]{3}$/.test(String(r.currency || "")) ? String(r.currency).toUpperCase() : BASE_CURRENCY;
    const payer = text(r.payer);
    const rate = Number(r.exchangeRate);
    const confidence = r.confidence === "alta" || r.confidence === "media" ? r.confidence : "baja";
    incomes.push({
      date,
      amount,
      currency,
      exchangeRate: currency !== BASE_CURRENCY && Number.isFinite(rate) && rate > 0 ? rate : null,
      payer: payer || "Sin identificar",
      payerType: INCOME_PAYER_TYPES.includes(r.payerType as IncomePayerType) ? (r.payerType as IncomePayerType) : "cliente",
      concept: text(r.concept),
      bank: text(r.bank, 80),
      confidence: payer && date ? confidence : "baja",
    });
  }
  const said = declaredDocumentKind(data.documentType);
  // Sin declarar y sin que la IA lo diga: un solo ingreso es una operación.
  const documentType = declared || said || (incomes.length > 1 ? "estado_de_cuenta" : "operacion");
  return {
    documentType,
    detectedType: said,
    mismatch: Boolean(declared && said && declared !== said),
    issuer: text(data.issuer, 120),
    account: text(data.account, 120),
    incomes,
    notes: text(data.notes, 500),
  };
}

type IncomeKey = Pick<Income, "date" | "amount" | "currency">;

/** Un ingreso ya registrado con la misma fecha, importe y moneda (o `null`). */
export function findDuplicateIncome(candidate: { date: string | null; amount: number; currency: string }, existing: readonly (IncomeKey & { id: string })[]): string | null {
  if (!candidate.date) return null;
  const hit = existing.find(
    (i) => i.date === candidate.date && String(i.currency || BASE_CURRENCY).toUpperCase() === candidate.currency && Math.abs(Number(i.amount) - candidate.amount) < 0.005,
  );
  return hit ? hit.id : null;
}

export type IncomePreview = ExtractedIncome & { index: number; duplicateOf: string | null };

/**
 * Numera los ingresos propuestos y marca los que ya están registrados. Dos
 * depósitos idénticos del mismo documento son dos ingresos: no se marcan.
 */
export function previewIncomes(incomes: readonly ExtractedIncome[], existing: readonly (IncomeKey & { id: string })[]): IncomePreview[] {
  return incomes.map((income, index) => ({ ...income, index, duplicateOf: findDuplicateIncome(income, existing) }));
}

export type IncomeMatch = IncomeKey & { id: string };

/**
 * Ingresos ya registrados con el mismo importe y moneda que alguno de los
 * propuestos, en cualquier fecha. Viajan con la propuesta para que, si la
 * persona corrige una fecha, el duplicado se vuelva a buscar sin otra llamada.
 */
export function incomeMatchCandidates(incomes: readonly ExtractedIncome[], existing: readonly IncomeMatch[]): IncomeMatch[] {
  return existing
    .filter((e) => incomes.some((i) => String(e.currency || BASE_CURRENCY).toUpperCase() === i.currency && Math.abs(Number(e.amount) - i.amount) < 0.005))
    .map((e) => ({ id: e.id, date: e.date, amount: Number(e.amount), currency: String(e.currency || BASE_CURRENCY).toUpperCase() }));
}
